import React, { useState, useContext, useEffect, useRef } from 'react';
import { TableContext } from '../context';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import '../App.css';
import { toast } from "react-toastify";
import { McSegmentedControl } from "@maersk-global/mds-react-wrapper/components-core/mc-segmented-control";
import { McSegmentedControlItem } from "@maersk-global/mds-react-wrapper/components-core/mc-segmented-control-item";
import { McTypeaheadMultiSelect } from "@maersk-global/mds-react-wrapper/components-core/mc-typeahead-multi-select";
import { McInput } from "@maersk-global/mds-react-wrapper/components-core/mc-input";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { McTag } from "@maersk-global/mds-react-wrapper/components-core/mc-tag";

// /**
//  * Debounce function to delay the execution of a function.
//  * @param {Function} func - The function to debounce.
//  * @param {number} wait - The delay in milliseconds.
//  */
// const debounce = (func, wait) => {
//   let timeout;
//   return (...args) => {
//     clearTimeout(timeout);
//     timeout = setTimeout(() => func(...args), wait);
//   };
// };

/**
 * Search component for searching application names.
 * Provides suggestions based on user input and allows fetching table data.
 */
function Search() {
  const { search, setSearch, subscribeOpen, setSubscribeOpen, inputValue, setInputValue, getToken, searchType, setSearchType } = useContext(TableContext);
  const location = useLocation();
  
  // Check if we're on the P1P2Incidents page
  const isP1P2IncidentsPage = location.pathname === '/P1P2Incidents';
  
  // On P1P2Incidents page, always use global search
  useEffect(() => {
    if (isP1P2IncidentsPage && searchType !== 'global') {
      setSearchType('global');
    }
  }, [isP1P2IncidentsPage, searchType, setSearchType]);
  
  // State for global search terms
  const [globalSearchTerms, setGlobalSearchTerms] = useState('');
  
  // State for application search (typeahead multi-select)
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [applicationSuggestions, setApplicationSuggestions] = useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  
  // Ref for the typeahead component
  const typeaheadRef = useRef(null);
  
  // Ref for the tags container to enable auto-scroll
  const tagsContainerRef = useRef(null);

  // Auto-scroll to the latest tag when new applications are added
  useEffect(() => {
    if (tagsContainerRef.current && selectedApplications.length > 0) {
      // Small delay to ensure the DOM has updated
      setTimeout(() => {
        tagsContainerRef.current.scrollTop = tagsContainerRef.current.scrollHeight;
      }, 100);
    }
  }, [selectedApplications.length]); // Only trigger when the count changes

  // Set up event listeners for the typeahead component
  useEffect(() => {
    const typeaheadElement = typeaheadRef.current;
    
    if (typeaheadElement) {
      // Add event listeners directly to the element like in reference
      const handleOptionSelected = (event) => {
        handleApplicationSelect(event);
        
        // Also check the component's selecteddata property after a short delay
        setTimeout(() => {
          if (typeaheadElement.selecteddata) {
            setSelectedApplications(typeaheadElement.selecteddata);
          }
        }, 100);
      };
      
      const handleSearch = (event) => {
        handleApplicationSearch(event);
      };
      
      // Also listen for any changes to selecteddata
      const handleSelectionChange = () => {
        if (typeaheadElement.selecteddata) {
          setSelectedApplications(typeaheadElement.selecteddata);
        }
      };
      
      typeaheadElement.addEventListener('optionselected', handleOptionSelected);
      typeaheadElement.addEventListener('search', handleSearch);
      
      // Set up a mutation observer to watch for changes to selecteddata
      const observer = new MutationObserver(() => {
        handleSelectionChange();
      });
      
      observer.observe(typeaheadElement, {
        attributes: true,
        attributeFilter: ['selecteddata'],
        childList: true,
        subtree: true
      });
      
      // Cleanup
      return () => {
        typeaheadElement.removeEventListener('optionselected', handleOptionSelected);
        typeaheadElement.removeEventListener('search', handleSearch);
        observer.disconnect();
      };
    }
  }, [searchType]); // Re-run when search type changes

  /**
   * Handles the segmented control change for search type selection
   */
  const handleSearchTypeChange = (e) => {
    const newSearchType = e.detail.item.value;
    setSearchType(newSearchType);
    
    // Clear data when switching search types
    if (newSearchType === "application") {
      setGlobalSearchTerms("");
      setSearch('');
    } else {
      setSelectedApplications([]);
      setApplicationSuggestions([]);
      setInputValue('');
      setSearch('');
    }
  };

  /**
   * Handles global search terms input change
   */
  const handleGlobalSearchInputChange = (e) => {
    setGlobalSearchTerms(e.target.value);
  };

  /**
   * Handles global search clear button click
   */
  const handleGlobalSearchClearClick = () => {
    setGlobalSearchTerms("");
    setSearch('');
    setSearch('');
  };

  /**
   * Handles typeahead clear button click
   */
  const handleTypeaheadClearClick = (e) => {
    // Clear all suggestions and data immediately
    setApplicationSuggestions([]);
    
    if (typeaheadRef.current) {
      typeaheadRef.current.data = [];
      typeaheadRef.current.loading = false;
      
      // Clear input value
      if (typeaheadRef.current.value !== undefined) {
        typeaheadRef.current.value = '';
      }
      
      // Clear the actual input element
      const inputElement = typeaheadRef.current.querySelector('input');
      if (inputElement) {
        inputElement.value = '';
      }
    }
    
    // Clear search context
    setInputValue('');
  };

  /**
   * Handles typeahead multi-select search for applications
   */
  const handleApplicationSearch = async (e) => {
    const searchValue = e.target.value;
    
    if (searchValue.length >= 3) {
      // Set loading state on the component directly like in reference
      if (typeaheadRef.current) {
        typeaheadRef.current.loading = true;
      }
      setIsLoadingSuggestions(true);
      
      try {
        const url = /^[Aa]\d{2,}$/.test(searchValue)
          ? `${process.env.REACT_APP_BACKEND_URI}/api/change_data/getAppIDSuggestions`
          : `${process.env.REACT_APP_BACKEND_URI}/api/change_data/getAppNameSuggestions`;

        const response = await axios.post(
          url, 
          { app_name: searchValue },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: "Bearer " + localStorage.getItem("jwt_token"),
            }
          }
        );

        if (response.data.status === 200) {
          const suggestions = response.data.data || [];
          const formattedSuggestions = suggestions.map(item => ({ 
            label: item, 
            value: item,
            id: item 
          }));
          
          setApplicationSuggestions(formattedSuggestions);
          
          // Set data directly on component like in reference
          if (typeaheadRef.current) {
            typeaheadRef.current.data = [...formattedSuggestions];
          }
        } else {
          setApplicationSuggestions([]);
          if (typeaheadRef.current) {
            typeaheadRef.current.data = [];
          }
        }
      } catch (error) {
        console.error('Error fetching application suggestions:', error);
        setApplicationSuggestions([]);
        if (typeaheadRef.current) {
          typeaheadRef.current.data = [];
        }
      } finally {
        setIsLoadingSuggestions(false);
        if (typeaheadRef.current) {
          typeaheadRef.current.loading = false;
        }
      }
    }
  };

  /**
   * Handles application selection from typeahead multi-select
   */
  const handleApplicationSelect = (e) => { 
    // Clear the data after selection like in the reference code
    setApplicationSuggestions([]);
    if (typeaheadRef.current) {
      typeaheadRef.current.data = [];
      typeaheadRef.current.loading = false;
    }
    
    // Update selected applications based on the event detail structure
    if (e.detail) {
      // Check all possible properties in the event detail
      if (e.detail.selecteddata) {
        setSelectedApplications(e.detail.selecteddata);
      } else if (e.detail.selectedData) {
        setSelectedApplications(e.detail.selectedData);
      } else if (e.detail.value) {
        // Handle single selection or array of values
        const currentSelections = selectedApplications || [];
        
        if (Array.isArray(e.detail.value)) {
          setSelectedApplications(e.detail.value);
        } else {
          // Add to existing selections if it's not already there
          const exists = currentSelections.some(item => 
            (typeof item === 'string' ? item : item.value) === 
            (typeof e.detail.value === 'string' ? e.detail.value : e.detail.value.value)
          );
          
          if (!exists) {
            const newItem = typeof e.detail.value === 'string' 
              ? { label: e.detail.value, value: e.detail.value, id: e.detail.value }
              : e.detail.value;
            setSelectedApplications([...currentSelections, newItem]);
          }
        }
      } else if (e.detail.option) {
        // Handle option property
        const currentSelections = selectedApplications || [];
        const newItem = e.detail.option;
        
        const exists = currentSelections.some(item => 
          (typeof item === 'string' ? item : item.value) === 
          (typeof newItem === 'string' ? newItem : newItem.value)
        );
        
        if (!exists) {
          setSelectedApplications([...currentSelections, newItem]);
        }
      } else {
        // Try to get selected data directly from the component
        if (typeaheadRef.current && typeaheadRef.current.selecteddata) {
          setSelectedApplications(typeaheadRef.current.selecteddata);
        }
      }
    }
  };

  /**
   * Removes an application from the selected list
   */
  const removeApplication = (index) => {
    const newSelectedApplications = selectedApplications.filter((_, i) => i !== index);
    setSelectedApplications(newSelectedApplications);
    
    // Also remove from the typeahead component if it has a removeSelectedOption method
    if (typeaheadRef.current && typeaheadRef.current.removeSelectedOption) {
      const appToRemove = selectedApplications[index];
      typeaheadRef.current.removeSelectedOption(appToRemove);
    }
    
    // Clear search if no applications left
    if (newSelectedApplications.length === 0) {
      setSearch('');
    }
  };

  /**
   * Clears all selected applications
   */
  const clearAllApplications = () => {
    setSelectedApplications([]);
    setApplicationSuggestions([]);
    setSearch('');
    
    // Clear the component data and selected data directly
    if (typeaheadRef.current) {
      typeaheadRef.current.data = [];
      // Clear selected data if the property exists
      if (typeaheadRef.current.selecteddata !== undefined) {
        typeaheadRef.current.selecteddata = [];
      }
    }
  };

  /**
   * Handles the click event of the subscribe button.
   */
  const handleSubscribeClick = () => {
    setSubscribeOpen(true);
  };

  /**
   * Handles the click event of the search button.
   * Works with both application and server search types.
   * On P1P2Incidents page, always uses global search.
   */
  const handleButtonClick = () => {
    if (searchType === 'application' && !isP1P2IncidentsPage) {
      if (selectedApplications.length === 0) {
        toast.error("Please select at least one application", {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
        return;
      }
      
      // Convert selected applications to comma-separated string
      const applicationNames = selectedApplications.map(app => app.value).join(', ');
      setSearch(applicationNames);
      setInputValue(applicationNames); // For compatibility
    } else {
      // Global search - client-side filtering
      if (!globalSearchTerms || globalSearchTerms.trim() === "") {
        toast.error("Please enter search terms", {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
        return;
      }
      
      // Set search terms for client-side filtering
      const cleanedSearchTerms = globalSearchTerms.trim();
      setSearch(cleanedSearchTerms);
      setInputValue(cleanedSearchTerms); // For compatibility
    }
  };

  return (
    <div className="search-container">
      <div className="search-bar" style={{ display: "flex", flexDirection: "row", gap: "16px", alignItems: "flex-start" }}>
        
        {/* Segmented Control for Search Type Selection - Hidden on P1P2Incidents page */}
        {!isP1P2IncidentsPage && (
          <div style={{ display: "flex", alignItems: "flex-start" }}>
            <McSegmentedControl
              fit="medium"
              listchange={handleSearchTypeChange}
            >
              <McSegmentedControlItem
                selected={searchType === "application"}
                value="application"
              >
                Application
              </McSegmentedControlItem>
              <McSegmentedControlItem value="global" selected={searchType === "global"}>
                Freelook
              </McSegmentedControlItem>
            </McSegmentedControl>
          </div>
        )}

        {/* Conditional Search Input based on Search Type */}
        <div style={{ flex: 1, maxWidth: "400px" }}>
          {(searchType === "application" && !isP1P2IncidentsPage) ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <McTypeaheadMultiSelect
                ref={typeaheadRef}
                hiddenlabel={true}
                hiddentags={true}
                placeholder="Search for application names/IDs"
                name="applications"
                minchars="3"
                disablefilter={true}
                clearbutton={true}
                clearbuttonclick={handleTypeaheadClearClick}
                optionsheight="200px"
                style={{
                  width: "100%"
                }}
                data={applicationSuggestions}
              />
              {/* Selected Applications Tags Container */}
              {selectedApplications.length > 0 && (
                <div 
                  ref={tagsContainerRef}
                  style={{
                    maxHeight: "30px",
                    overflowY: "auto",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "6px",
                    scrollBehavior: "smooth",
                    alignItems: "center"
                  }}
                >
                  {selectedApplications.map((app, index) => (
                    <McTag
                      key={index}
                      appearance="neutral-weak"
                      dismiss={(e) => removeApplication(index)}
                      withaction
                    >
                      {typeof app === 'string' ? app : app.value || app.label}
                    </McTag>
                  ))}
                  {/* Clear all button - show when 2 or more items selected */}
                  {selectedApplications.length >= 2 && (
                    <button
                      type="button"
                      onClick={clearAllApplications}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#0077b2",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "500",
                        padding: "4px 8px",
                        whiteSpace: "nowrap",
                      }}
                      onMouseOver={(e) => e.target.style.color = "#0077b2"}
                      onMouseOut={(e) => e.target.style.color = "#0077b2"}
                    >
                      Clear all
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <McInput
              style={{
                width: "100%"
              }}
              clearbutton
              input={handleGlobalSearchInputChange}
              clearbuttonclick={handleGlobalSearchClearClick}
              label="Global Search"
              value={globalSearchTerms}
              hiddenlabel
              placeholder="Search across all fields (comma-separated terms)"
              autocomplete="off"
              name="globalsearch"
            />
          )}
        </div>

        {/* Search and Subscribe Buttons */}
        <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
          <McButton 
            click={handleButtonClick} 
            appearance="primary"
          >
            Search
          </McButton>
          
          {search !== '' && searchType === 'application' && (
            <McButton 
              click={handleSubscribeClick}
              appearance="neutral"
              variant="outlined"
            >
              Subscribe
            </McButton>
          )}
        </div>
      </div>
    </div>
  );
}

export default Search;